import React, { createContext, useContext, useCallback, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { globalHotkeyDefinitions, hotkeyOptions, saveHotkeyOptions } from '@/app/config/hotkeys'
import { HotkeysHelp } from './HotkeysHelp'

interface GlobalHotkeysContextType {
  registerSaveHandler: (handler: () => void) => void
  unregisterSaveHandler: () => void
  showHotkeysList: () => void
}

const GlobalHotkeysContext = createContext<GlobalHotkeysContextType | undefined>(undefined)

export const useGlobalHotkeys = () => {
  const context = useContext(GlobalHotkeysContext)
  if (!context) {
    throw new Error('useGlobalHotkeys must be used within GlobalHotkeysProvider')
  }
  return context
}

interface GlobalHotkeysProviderProps {
  children: React.ReactNode
}

export const GlobalHotkeysProvider: React.FC<GlobalHotkeysProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const saveHandlerRef = useRef<(() => void) | null>(null)
  const [showHelpDialog, setShowHelpDialog] = useState(false)

  const registerSaveHandler = useCallback((handler: () => void) => {
    saveHandlerRef.current = handler
  }, [])

  const unregisterSaveHandler = useCallback(() => {
    saveHandlerRef.current = null
  }, [])

  const showHotkeysList = useCallback(() => {
    setShowHelpDialog(true)
  }, [])



  // Save hotkey (Ctrl+S / Cmd+S)
  useHotkeys(
    globalHotkeyDefinitions.save.keys,
    (e) => {
      e.preventDefault()
      if (saveHandlerRef.current) {
        saveHandlerRef.current()
      } else {
        toast.info('No form to save on this page')
      }
    },
    saveHotkeyOptions
  )

  // New Sales Invoice (Alt+S / Option+S)
  useHotkeys(
    globalHotkeyDefinitions.newSales.keys,
    (e) => {
      e.preventDefault()
      navigate(globalHotkeyDefinitions.newSales.route!)
      toast.success('Opening new sales invoice')
    },
    hotkeyOptions
  )

  // Payment In (Alt+I / Option+I)
  useHotkeys(
    globalHotkeyDefinitions.paymentIn.keys,
    (e) => {
      e.preventDefault()
      navigate(globalHotkeyDefinitions.paymentIn.route!)
      toast.success('Opening payment in')
    },
    hotkeyOptions
  )

  // Payment Out (Alt+O / Option+O) - Using expenses as payment out
  useHotkeys(
    globalHotkeyDefinitions.paymentOut.keys,
    (e) => {
      e.preventDefault()
      navigate(globalHotkeyDefinitions.paymentOut.route!)
      toast.success('Opening payment out (expenses)')
    },
    hotkeyOptions
  )

  // New Estimate/Quotation (Alt+M / Option+M)
  useHotkeys(
    globalHotkeyDefinitions.estimate.keys,
    (e) => {
      e.preventDefault()
      navigate(globalHotkeyDefinitions.estimate.route!)
      toast.success('Opening new quotation')
    },
    hotkeyOptions
  )

  // Expenses (Alt+E / Option+E)
  useHotkeys(
    globalHotkeyDefinitions.expenses.keys,
    (e) => {
      e.preventDefault()
      navigate(globalHotkeyDefinitions.expenses.route!)
      toast.success('Opening expenses')
    },
    hotkeyOptions
  )

  // Reports (Alt+R / Option+R) - Using dashboard as reports
  useHotkeys(
    globalHotkeyDefinitions.report.keys,
    (e) => {
      e.preventDefault()
      navigate(globalHotkeyDefinitions.report.route!)
      toast.success('Opening reports (dashboard)')
    },
    hotkeyOptions
  )

  // Help hotkey (? or Shift+/)
  useHotkeys(
    'shift+/',
    (e) => {
      e.preventDefault()
      showHotkeysList()
    },
    { ...hotkeyOptions, enableOnFormTags: true }
  )

  // Alternative help hotkey
  useHotkeys(
    'alt+h',
    (e) => {
      e.preventDefault()
      setShowHelpDialog(true)
    },
    hotkeyOptions
  )

  return (
    <GlobalHotkeysContext.Provider
      value={{
        registerSaveHandler,
        unregisterSaveHandler,
        showHotkeysList,
      }}
    >
      {children}
      <HotkeysHelp open={showHelpDialog} onOpenChange={setShowHelpDialog} />
    </GlobalHotkeysContext.Provider>
  )
}